import { useTranslate } from '@refinedev/core';
import {
  Card,
  Flex,
  Grid,
  Progress,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { CycleDetailOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { CycleKindTag, CycleStatusTag } from '@/components/settlement/tags';
import { formatFraction } from '@/utils/format';
import { CYCLE_SHOW_TABS } from '../constants';
import type { CycleShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<CycleShowTab, string> = {
  general: 'base.sections.basic_info',
  delta_log: 'settlement.cycles.sections.delta_log',
};

interface CycleShowHeaderProps {
  record: CycleDetailOut;
  activeTab: CycleShowTab;
  onTabChange: (key: string) => void;
}

export const CycleShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: CycleShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Title
            level={3}
            style={{ margin: 0, fontSize: token.fontSizeHeading3 }}
          >
            {`#${record.id}`}
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t('settlement.cycles.titles.show')} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('settlement.cycles.fields.cycle_kind')}>
              <CycleKindTag value={record.cycle_kind} />
            </StatCell>
            <StatCell label={t('settlement.cycles.fields.pass_number')}>
              {`${record.pass_number}`}
            </StatCell>
            <StatCell label={t('settlement.cycles.fields.max_billable_cycles')}>
              {`${record.max_billable_cycles}`}
            </StatCell>
            <StatCell label={t('settlement.cycles.fields.is_billable')}>
              <Tag color={record.is_billable ? 'green' : 'default'}>
                {t(record.is_billable ? 'common.yes' : 'common.no')}
              </Tag>
            </StatCell>
            <StatCell label={t('settlement.cycles.fields.coverage')}>
              <Tooltip title={record.covered_fraction}>
                <Progress
                  percent={Math.round(Number(record.covered_fraction) * 100)}
                  format={() => formatFraction(record.covered_fraction)}
                  style={{ width: 180, marginBottom: 0 }}
                />
              </Tooltip>
            </StatCell>
            <StatCell label={t('settlement.cycles.fields.status')}>
              <CycleStatusTag
                status={record.status}
                closeReason={record.close_reason}
              />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={CYCLE_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
